import React from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard,
  MessageSquare,
  Settings,
  LogOut,
  ChevronLeft,
  Mail,
  Users,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../contexts/AuthContext";

const sidebarLinks = [
  { label: "Quotes", path: "/admin/dashboard", icon: MessageSquare, permission: "quotes" },
  { label: "Site Config", path: "/admin/site-config", icon: Settings, permission: "site_config" },
  { label: "Email Settings", path: "/admin/email-config", icon: Mail, permission: "email_config" },
  { label: "Users", path: "/admin/users", icon: Users, permission: "user_management" },
];

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get user permissions (super_admin has all permissions)
  const permissions = admin?.permissions || [];
  const roles = admin?.roles || [];
  const isSuperAdmin = roles.includes("super_admin");

  const handleLogout = () => {
    logout();
    navigate("/admin");
  };
  
  // Filter sidebar links based on permissions
  const visibleLinks = sidebarLinks.filter(link => 
    isSuperAdmin || permissions.includes(link.permission)
  );

  return (
    <div className="min-h-screen bg-[#0A0A0F] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0D0D14] border-r border-white/[0.04] flex flex-col shrink-0">
        <div className="p-6 border-b border-white/[0.04]">
          <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-white text-xs mb-4 transition-colors">
            <ChevronLeft className="h-3 w-3" /> Back to Site
          </Link>
          <h2 className="text-white font-semibold text-lg">Admin Panel</h2>
          <p className="text-gray-500 text-xs mt-1">Logged in as <span className="text-[#DC2626]">{admin?.username}</span></p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {visibleLinks.map((link) => {
            const Icon = link.icon;
            const isActive = location.pathname === link.path;
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-[#DC2626]/10 text-[#DC2626]"
                    : "text-gray-400 hover:text-white hover:bg-white/[0.04]"
                }`}
              >
                <Icon className="h-4 w-4" />
                {link.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/[0.04]">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/[0.04] px-4"
          >
            <LogOut className="h-4 w-4 mr-3" /> Sign Out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
